import { Button } from '@mantine/core';
import { AlertTriangle } from 'lucide-react';
import { FC, ReactElement, useMemo } from 'react';

import NullComponent from '@/components/NullComponent.tsx';

import { CardReaderStep } from '@/models/CardReader.ts';
import { ModalType } from '@/models/Modal.ts';
import { CardReadActionType } from '@/models/doc-cirrus/DCCardReader.ts';

import { closeModal } from '@/services/modalService.tsx';
import { showErrorNotification } from '@/services/notificationService.ts';

import { useWalkInStore } from '@/stores/walkInStore.ts';

import { applyCardReadAction } from '@/utils/cardReadUtils.ts';

interface Props {
    expectedPvsUserId: string;
    customerId: string;
    onSuccess: () => void;
}

const CardReadModalFooter: FC<Props> = ({ expectedPvsUserId, customerId, onSuccess }) => {
    const cardReadState = useWalkInStore((state) => state.card_read_state);
    const isLoading = useWalkInStore((state) => state.is_loading);

    const isPatientMatch = useMemo((): boolean => {
        if (!cardReadState?.patients[0]) return false;
        return cardReadState.patients[0].user_id === expectedPvsUserId;
    }, [cardReadState?.patients, expectedPvsUserId]);

    const handleSuccessConfirm = (): void => {
        closeModal(ModalType.CARD_READ);
        onSuccess();
    };

    const handleRetry = (): void => {
        useWalkInStore.setState({
            card_read_state: {
                ...cardReadState,
                step: CardReaderStep.SELECT_READER,
                patients: [],
                possible_actions: [],
                op_id: '',
            },
        });
    };

    const buildFooterContent = (): ReactElement => {
        switch (cardReadState?.step) {
            case CardReaderStep.SUCCESS: {
                if (isPatientMatch) {
                    return (
                        <div className="flex w-full justify-end">
                            <Button disabled={isLoading} onClick={handleSuccessConfirm}>
                                Fertig
                            </Button>
                        </div>
                    );
                }

                return (
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                            <AlertTriangle className="size-5 shrink-0 text-amber-600" />
                            <p className="text-sm text-amber-800">
                                Die gelesene Karte gehört nicht zu diesem Patienten. Bitte versuchen Sie es erneut oder
                                schließen Sie das Fenster.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="subtle" onClick={() => closeModal(ModalType.CARD_READ)}>
                                Schließen
                            </Button>
                            <Button onClick={handleRetry}>Erneut versuchen</Button>
                        </div>
                    </div>
                );
            }

            case CardReaderStep.MERGED: {
                if (!isPatientMatch) {
                    return (
                        <div className="flex w-full flex-col gap-4">
                            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                <AlertTriangle className="size-5 shrink-0 text-amber-600" />
                                <p className="text-sm text-amber-800">
                                    Die gelesene Karte gehört nicht zu diesem Patienten.
                                </p>
                            </div>
                            <div className="flex justify-end gap-3">
                                <Button variant="subtle" onClick={() => closeModal(ModalType.CARD_READ)}>
                                    Schließen
                                </Button>
                                <Button onClick={handleRetry}>Erneut versuchen</Button>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="flex w-full flex-row justify-between">
                        {cardReadState.possible_actions.includes(CardReadActionType.CANCEL) && (
                            <Button
                                disabled={isLoading}
                                variant="outline"
                                onClick={async () => {
                                    try {
                                        await applyCardReadAction(
                                            cardReadState,
                                            cardReadState.patients[0],
                                            CardReadActionType.CANCEL,
                                            customerId
                                        );
                                    } catch {
                                        useWalkInStore.setState({ is_loading: false });
                                        showErrorNotification('Vorgang konnte nicht abgebrochen werden.');
                                    }
                                }}
                            >
                                Vorgang abbrechen
                            </Button>
                        )}
                        {cardReadState.possible_actions.includes(CardReadActionType.APPLY) && (
                            <Button
                                disabled={isLoading}
                                onClick={async () => {
                                    try {
                                        await applyCardReadAction(
                                            cardReadState,
                                            cardReadState.patients[0],
                                            CardReadActionType.APPLY,
                                            customerId
                                        );
                                    } catch {
                                        useWalkInStore.setState({ is_loading: false });
                                        showErrorNotification('Patientenakte konnte nicht aktualisiert werden.');
                                    }
                                }}
                            >
                                Patientenakte aktualisieren
                            </Button>
                        )}
                    </div>
                );
            }

            case CardReaderStep.MATCHING: {
                return cardReadState?.patients[0] &&
                    cardReadState.possible_actions.includes(CardReadActionType.CANCEL) ? (
                    <div className="flex w-full flex-row justify-between">
                        <Button
                            disabled={isLoading}
                            variant="outline"
                            onClick={async () => {
                                try {
                                    await applyCardReadAction(
                                        cardReadState,
                                        cardReadState.patients[0],
                                        CardReadActionType.CANCEL,
                                        customerId
                                    );
                                } catch {
                                    useWalkInStore.setState({ is_loading: false });
                                    showErrorNotification('Vorgang konnte nicht abgebrochen werden.');
                                }
                            }}
                        >
                            Vorgang abbrechen
                        </Button>
                    </div>
                ) : (
                    <NullComponent />
                );
            }

            case CardReaderStep.RETRY:
            case CardReaderStep.CANCELLED: {
                return (
                    <div className="flex w-full flex-row justify-end gap-3">
                        <Button variant="subtle" onClick={() => closeModal(ModalType.CARD_READ)}>
                            Schließen
                        </Button>
                        <Button disabled={isLoading} onClick={handleRetry}>
                            {cardReadState.step === CardReaderStep.CANCELLED ? 'Neu starten' : 'Noch einmal versuchen'}
                        </Button>
                    </div>
                );
            }

            default: {
                return <NullComponent />;
            }
        }
    };

    return buildFooterContent();
};

export default CardReadModalFooter;
